import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../lib/utils';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center whitespace-nowrap rounded',
    'font-outfit text-[14px] font-medium capitalize leading-[1.2]',
    'transition-colors duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-dawn-orange text-white',
          'hover:bg-dawn-orange-hover',
          'disabled:bg-grey-200 disabled:text-grey-400',
        ],
        destructive: [
          'bg-error text-white',
          'hover:bg-error/90',
          'disabled:bg-grey-200 disabled:text-grey-400',
        ],
        outline: [
          'border border-grey-400 bg-transparent text-dawn-black',
          'hover:bg-grey-100',
          'disabled:opacity-50',
        ],
        secondary: ['bg-grey-100 text-dawn-black', 'hover:bg-grey-200', 'disabled:opacity-50'],
        ghost: ['text-dawn-black', 'hover:bg-grey-100', 'disabled:opacity-50'],
        link: ['text-grey-400', 'hover:text-dawn-black', 'disabled:opacity-50'],
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 px-3',
        lg: 'h-11 px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Optional leading icon. Rendered to the left of `children` with a 2px gap
   * (`gap-0.5`). Most useful with `variant="link"` to reproduce the Figma
   * "Secondary + Icon" pattern (`289:49599`) — Show More links, "View detail",
   * etc. Works on every variant.
   */
  leadingIcon?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, leadingIcon, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    // `Slot` (asChild) requires exactly one child element — when asChild is on,
    // forward `children` as-is and skip the leadingIcon wrapper entirely. The
    // leadingIcon prop is the Figma "Secondary + Icon" pattern, which always
    // renders as a real <button>, never as a Slot proxy.
    const content = asChild ? (
      children
    ) : (
      <>
        {leadingIcon ? (
          <span className="inline-flex items-center justify-center">{leadingIcon}</span>
        ) : null}
        {children}
      </>
    );
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), 'gap-0.5')}
        ref={ref}
        {...props}
      >
        {content}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
